import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

// ------ Translation dictionary (UI strings shared across components) ------
const dict = {
  en: {
    nav: {
      services: 'Services',
      automation: 'Automation',
      aiAgents: '+ AI Agents',
      indexAi: '+ Index AI',
      company: 'Company',
      blog: 'Resources',
      magazine: 'Magazine',
      faq: 'FAQ',
      searchPlaceholder: 'Search in ...',
    },
    hero: {
      eyebrow: 'RESTAURANT OPERATING INTELLIGENCE',
      titleA: 'Your next customer just asked AI where to eat.',
      titleB: 'Make sure the answer is your restaurant.',
      build: 'Build.',
      optimize: 'Optimize.',
      scale: 'Scale.',
      body: 'Your team cooks and takes care of the room. Kitxens observes, learns and sells — 24/7, on every channel. The more it learns about your restaurant, the harder it works for you. That is symbiosis.',
      credibility: 'Built for independent restaurants, multi-location groups and hospitality brands across North America, Europe and Latin America.',
      philosophy: 'AI where it matters. Human expertise where it creates real value.',
      ctaPrimary: 'Request Free Audit',
      ctaSecondary: 'Meet the Intelligence',
      dashboardTitleA: 'AI Dashboard',
      dashboardTitleB: 'for Business Growth',
      dashboardSubtitle: 'Learning from your operation right now.',
      yourTools: 'YOUR TOOLS',
      yourResults: 'YOUR RESULTS',
      kpis: { revenue: 'Revenue', orders: 'Orders', avgTicket: 'Avg. Ticket', retention: 'Retention' },
    },
    trusted: {
      heading: 'Works with the restaurant technology',
      headingAccent: 'you already use.',
      body: 'Kitxens connects to the systems running your restaurant today — POS, delivery, reservations, marketing and payments — so every operation feeds one intelligence that learns.',
      label: 'ONE INTELLIGENCE,',
      labelAccent: 'EVERY SYSTEM CONNECTED.',
      andMore: 'and 40+ more platforms.',
      reassurance: 'No rip-and-replace. No expensive migrations. Kitxens layers on top of the technology you already have.',
      trustLine: 'Designed for independent restaurants and multi-location hospitality groups across North America, Europe and Latin America.',
      statusConnected: 'Native Integration',
      statusSupported: 'Managed Integration',
      statusSoon: 'Certified Compatibility',
      statusNative: 'Native Integration',
      statusManaged: 'Managed Integration',
      statusCertified: 'Certified Compatibility',
      legendTitle: 'Integration model',
      legendNative: 'Native — first-party connector built and maintained by Kitxens.',
      legendManaged: 'Managed — Kitxens operates the integration for you through certified APIs and middleware.',
      legendCertified: 'Certified — tested and supported through standard APIs, webhooks or partner connectors.',
      categories: [
        { id: 'operations', label: 'Restaurant Operations', desc: 'POS, kitchen, inventory and the technology stack that runs your day-to-day.' },
        { id: 'visibility', label: 'Guest Acquisition & Visibility', desc: 'Search, AI engines, social and reviews — where new guests find you.' },
        { id: 'delivery', label: 'Online Ordering & Delivery', desc: 'Direct ordering and marketplaces that move digital revenue.' },
        { id: 'reservations', label: 'Reservations & Catering', desc: 'Tables, events and corporate catering — managed and centralized.' },
        { id: 'payments', label: 'Payments & Commerce', desc: 'Process, reconcile and grow revenue across every channel.' },
      ],
      outcomesEyebrow: 'BUSINESS OUTCOMES',
      outcomesTitle: 'Five measurable outcomes the intelligence delivers.',
      outcomesBody: 'One intelligence that learns. Five outcomes that move the business — not generic features.',
      crossSellLabel: 'Explore',
    },
    engines: {
      eyebrow: 'THE OPERATING INTELLIGENCE',
      titleA: 'One intelligence.',
      titleB: 'Applied to every area of your business.',
      subtitle: 'It observes every order, review and search — and every week it acts a little smarter than the last. Each capability feeds the others.',
      cta: 'Meet the Intelligence',
      microStat: {
        engines: 'connected engines',
        integrations: 'integrations',
        intelligence: 'operating intelligence',
      },
      badges: {
        foundation: 'FOUNDATION',
        flagship: 'FLAGSHIP',
        bestseller: 'BEST SELLER',
        popular: 'MOST POPULAR',
        enterprise: 'ENTERPRISE',
      },
    },
    challenges: {
      eyebrow: 'THE 5 BIGGEST PROBLEMS RESTAURANTS FACE',
      title: 'We built one intelligence',
      titleAccent: 'to solve all of them.',
      intro: 'Real operators face the same five problems every day. Each one has a cost — missed revenue, slower growth, wasted hours. Kitxens solves them as one intelligence that learns from your whole operation, not as five separate tools.',
      solutionLabel: 'Solved by',
      notSure: 'Not sure which problem to start with?',
      auditCta: 'Get Your Free Restaurant Audit',
    },
    comparison: {
      eyebrow: 'MEASURABLE RESULTS',
      titleA: 'What Restaurants Achieve with',
      titleAccent: 'Kitxens.',
      body: 'Technology, automation and visibility working together to drive measurable growth.',
      testimonialEyebrow: 'WHAT RESTAURANT OWNERS SAY',
    },
    insights: {
      eyebrow: 'FROM THE KITXENS MAGAZINE',
      titleA: 'Restaurant Operating',
      titleAccent: 'Intelligence.',
      body: 'Editorial perspectives on Restaurant AI, technology, operations and growth — written by the team building the operating system for modern restaurants.',
      readMore: 'Read article',
      nextPage: 'Explore the Magazine',
    },
    audit: {
      eyebrow: 'FREE RESTAURANT GROWTH AUDIT',
      titleA: 'Ready to Accelerate Your',
      titleAccent: 'Restaurant Growth?',
      body: 'Discover hidden opportunities to increase sales, improve visibility, automate operations and grow your restaurant faster.',
      directPhone: 'Direct Phone',
      emailInquiry: 'Email Inquiry',
      formEyebrow: 'KITXENS FORM',
      firstName: 'First name',
      lastName: 'Last name',
      workEmail: 'Work email',
      restaurant: 'Restaurant',
      interested: 'Interested In...',
      selectSolution: 'Select a solution',
      phone: 'Phone',
      additional: 'Additional information',
      submit: 'Get My Free Restaurant Audit',
      disclaimer: 'No obligation. No credit card required. A Kitxens specialist will review your opportunities and get back to you personally.',
      thanks: 'Thank you!',
      thanksBody: 'Your free restaurant audit request has been received. A Kitxens specialist will reach out personally within 24 hours.',
      submitAnother: 'Submit another request',
      missingTitle: 'Missing required fields',
      missingBody: 'Please fill in your first name and work email.',
      successTitle: 'Audit request received',
      successBody: 'A Kitxens specialist will reach out shortly.',
      errorTitle: "Couldn't send your request",
      errorBody: 'Something went wrong on our side. Please try again in a moment.',
      interests: ['IT & POS Systems', 'AI & Automation', 'Index AI / Visibility', 'Revenue Growth', 'Data & Analytics', 'Not sure yet'],
    },
    cookies: {
      body: 'We use cookies on our website to see how you interact with it. By accepting, you agree to our use of such cookies.',
      privacy: 'Privacy Policy',
      settings: 'Settings',
      accept: 'Accept',
    },
    footer: {
      tagline: 'One technology partner for every restaurant solution. Build, optimize and scale with a connected ecosystem.',
      solutions: 'Solutions',
      company: 'Company',
      contact: 'Contact',
      rights: 'All rights reserved.',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      cookies: 'Cookies',
      solutionsList: [
        { label: 'IT & POS Systems', href: '/it-pos-systems' },
        { label: 'AI & Automation', href: '/ai-and-automation' },
        { label: 'Digital Branding', href: '/digital-branding' },
        { label: 'Revenue Growth', href: '/restaurant-revenue-growth' },
        { label: 'All Services', href: '/services' },
      ],
    },
    outcomes: [
      { title: 'Save Time', subtitle: 'Automate the repetitive work', cross: 'AI Agents', crossHref: '/ai-agents-for-restaurants' },
      { title: 'Increase Visibility', subtitle: 'Be the answer AI engines recommend', cross: 'Index AI', crossHref: '/index-ai' },
      { title: 'Grow Catering Revenue', subtitle: 'Capture corporate and event leads', cross: 'Catering AI', crossHref: '/contact', soon: true },
      { title: 'Reduce Operating Costs', subtitle: 'Less manual work. Fewer disconnected tools.' },
      { title: 'Make Smarter Decisions', subtitle: 'Real-time dashboards. Actionable insights.' },
    ],
    services: [
      { title: 'Restaurant Technology', capability: 'Foundation', badge: 'FOUNDATION', description: 'The connected POS, cloud and IT foundation every modern restaurant runs on.' },
      { title: 'Index AI', capability: 'Visibility', badge: 'FLAGSHIP', description: 'Be the restaurant AI engines and search recommend — across Google, Maps and generative answers.' },
      { title: 'AI Agents', capability: 'Automation', badge: 'BEST SELLER', description: 'Always-on AI agents that automate operations, guest communication and back-office work.' },
      { title: 'Revenue Growth', capability: 'Growth', badge: 'MOST POPULAR', description: 'Grow orders, ticket size and repeat visits across every channel — direct, delivery and dine-in.' },
      { title: 'Data & Analytics', capability: 'Intelligence', badge: 'ENTERPRISE', description: 'Real-time dashboards and restaurant-grade analytics for smarter, faster decisions.' },
    ],
    challengeCards: [
      {
        question: 'Losing calls?',
        title: 'Never miss another call.',
        description: 'Reservations, takeout and event inquiries answered 24/7, in your brand voice — no more dropped revenue or annoyed guests.',
        solution: 'AI Receptionist',
        solutionHref: '/ai-agents-for-restaurants',
      },
      {
        question: 'Invisible online?',
        title: 'Be found before your competitors.',
        description: 'Become the restaurant Google, ChatGPT, Gemini and Perplexity recommend first — across local search and AI answers.',
        solution: 'Index AI',
        solutionHref: '/index-ai',
      },
      {
        question: 'Drowning in manual work?',
        title: 'Eliminate the busywork.',
        description: 'Always-on AI agents handle reviews, reports, follow-ups and back-office tasks your team shouldn’t be doing.',
        solution: 'AI Agents',
        solutionHref: '/ai-agents-for-restaurants',
      },
      {
        question: 'Bleeding on delivery fees?',
        title: 'Grow direct revenue.',
        description: 'More direct orders, bigger tickets, more repeat visits. Less dependence on third-party marketplaces.',
        solution: 'Revenue Growth',
        solutionHref: '/restaurant-revenue-growth',
      },
      {
        question: 'Flying blind on the numbers?',
        title: 'Make smarter decisions, faster.',
        description: 'Real-time dashboards that turn POS, delivery and marketing data into the next move — without hiring an analyst.',
        solution: 'Data & Analytics',
        solutionHref: '/services',
      },
    ],
    comparisonCols: [
      {
        title: 'Most restaurants hire different providers.',
        points: [
          'A dedicated team of one or two employees or managers focused on IT, POS and resolving technical issues, often leading to higher costs.',
          'Without an expert team, or relying on just two or three partners, achieving optimal customer engagement, frequency and revenue can be challenging.',
          'Managing and optimizing your digital footprint independently will be costly, resource-intensive and risky, leading to missed opportunities.',
          'Traditional restaurants without IT specialists face challenges in managing AI, data analytics and digital engagement.',
        ],
      },
      {
        title: 'Kitxens brings everything together.',
        highlighted: true,
        points: [
          'Our cloud-based IT department provides comprehensive, seamless support for all your IT, POS and technical issue needs.',
          'A dedicated and skilled team focused on customer interactions, frequency and revenue growth. One phone, one e-mail.',
          'Partnering with Kitxens ensures expert management of your digital footprint, safeguarding data and reducing costs.',
          'Specialized expertise in AI, data analytics and digital engagement to optimize operations and stay competitive.',
        ],
      },
    ],
    testimonial: {
      quote: "With Kitxens, the average check size is up 15%. That's another $120,000 per year when we're at full capacity.",
      author: 'Wayne Carrington',
      role: "Owner at Roc \u2018N\u2019 Ramen",
    },
    common: {
      learnMore: 'Learn more',
      requestAudit: 'Request Free Audit',
      talkSpecialist: 'Talk to a Specialist',
    },
    operatingIntelligence: {
      eyebrow: 'MEET THE INTELLIGENCE BEHIND KITXENS',
      titleA: 'One operating team.',
      titleB: 'Six AI specialists. Always on.',
      body: 'Every restaurant works with one dedicated point of contact, supported by a growing network of specialized AI teammates operating behind the scenes.',
      liveLabel: 'LIVE',
      nowLabel: 'NOW',
      onlineLabel: 'Online',
      meet: 'Meet',
      summary: '6 AI Specialists Online · Human-led · Continuously expanding',
      cta: 'Explore the Operating Team',
      agents: [
        { name: 'Rachel', role: 'Client Concierge', now: 'Handling guest conversations', metric: 'conversations today' },
        { name: 'Stan', role: 'Lead Generation', now: 'Prospecting corporate catering accounts', metric: 'catering leads discovered' },
        { name: 'Eva', role: 'Operations', now: 'Coordinating workflows', metric: 'workflows coordinated' },
        { name: 'Sonny', role: 'Social Media', now: 'Scheduling campaigns', metric: 'campaigns scheduled' },
        { name: 'Penny', role: 'SEO Content', now: 'Publishing visibility content', metric: 'articles published' },
        { name: 'Linda', role: 'Legal', now: 'Preparing operational documents', metric: 'documents prepared' },
      ],
    },
    footerBrand: {
      line1: 'One Intelligence.',
      line2: 'Built for Restaurants.',
      line3: 'Powered by AI.',
      line4: 'Operated by Experts.',
    },
    footerBrief: {
      title: 'Restaurant Technology Brief',
      subtitle: 'Monthly insights for ambitious restaurant operators.',
    },
  },
  es: {
    nav: {
      services: 'Servicios',
      automation: 'Automatización',
      aiAgents: '+ Agentes de IA',
      indexAi: '+ Index AI',
      company: 'Empresa',
      blog: 'Recursos',
      magazine: 'Magazine',
      faq: 'Preguntas',
      searchPlaceholder: 'Buscar en ...',
    },
    hero: {
      eyebrow: 'INTELIGENCIA OPERATIVA PARA RESTAURANTES',
      titleA: 'Tu próximo cliente acaba de preguntarle a la IA dónde comer.',
      titleB: 'Asegúrate de que la respuesta sea tu restaurante.',
      build: 'Crea.',
      optimize: 'Optimiza.',
      scale: 'Escala.',
      body: 'Tu equipo cocina y cuida la sala. Kitxens observa, aprende y vende — 24/7, en cada canal. Cuanto más aprende de tu restaurante, más trabaja para ti. Eso es simbiosis.',
      credibility: 'Construida para restaurantes independientes, grupos multi-local y marcas de hospitalidad en Norteamérica, Europa y Latinoamérica.',
      philosophy: 'IA donde importa. Expertise humano donde genera valor real.',
      ctaPrimary: 'Solicita una Auditoría Gratis',
      ctaSecondary: 'Conoce la Inteligencia',
      dashboardTitleA: 'Panel de IA',
      dashboardTitleB: 'para el Crecimiento del Negocio',
      dashboardSubtitle: 'Aprendiendo de tu operación ahora mismo.',
      yourTools: 'TUS HERRAMIENTAS',
      yourResults: 'TUS RESULTADOS',
      kpis: { revenue: 'Ingresos', orders: 'Pedidos', avgTicket: 'Ticket Prom.', retention: 'Retención' },
    },
    trusted: {
      heading: 'Funciona con la tecnología',
      headingAccent: 'que tu restaurante ya usa.',
      body: 'Kitxens se conecta a los sistemas que ya operan tu restaurante — POS, delivery, reservas, marketing y pagos — para que cada operación alimente una única inteligencia que aprende.',
      label: 'UNA INTELIGENCIA,',
      labelAccent: 'CADA SISTEMA CONECTADO.',
      andMore: 'y 40+ plataformas más.',
      reassurance: 'Sin reemplazar nada. Sin migraciones costosas. Kitxens se monta sobre la tecnología que ya tienes.',
      trustLine: 'Diseñado para restaurantes independientes y grupos de hospitalidad multi-local en Norteamérica, Europa y Latinoamérica.',
      statusConnected: 'Integración Nativa',
      statusSupported: 'Integración Gestionada',
      statusSoon: 'Compatibilidad Certificada',
      statusNative: 'Integración Nativa',
      statusManaged: 'Integración Gestionada',
      statusCertified: 'Compatibilidad Certificada',
      legendTitle: 'Modelo de integración',
      legendNative: 'Nativa — conector de primera parte construido y mantenido por Kitxens.',
      legendManaged: 'Gestionada — Kitxens opera la integración por ti mediante APIs certificadas y middleware.',
      legendCertified: 'Certificada — compatible y soportada vía APIs estándar, webhooks o conectores partner.',
      categories: [
        { id: 'operations', label: 'Operaciones del Restaurante', desc: 'POS, cocina, inventario y la tecnología que sostiene tu día a día.' },
        { id: 'visibility', label: 'Captación y Visibilidad', desc: 'Búsqueda, motores de IA, redes y reseñas — donde tus nuevos clientes te encuentran.' },
        { id: 'delivery', label: 'Pedidos Online y Delivery', desc: 'Pedidos directos y marketplaces que mueven el ingreso digital.' },
        { id: 'reservations', label: 'Reservas y Catering', desc: 'Mesas, eventos y catering corporativo — gestionados y centralizados.' },
        { id: 'payments', label: 'Pagos y Comercio', desc: 'Procesa, concilia y haz crecer ingresos en todos los canales.' },
      ],
      outcomesEyebrow: 'RESULTADOS DE NEGOCIO',
      outcomesTitle: 'Cinco resultados medibles que entrega la inteligencia.',
      outcomesBody: 'Una inteligencia que aprende. Cinco resultados que mueven el negocio — no funciones genéricas.',
      crossSellLabel: 'Conoce',
    },
    engines: {
      eyebrow: 'LA INTELIGENCIA OPERATIVA',
      titleA: 'Una inteligencia.',
      titleB: 'Aplicada a cada área de tu negocio.',
      subtitle: 'Observa cada pedido, reseña y búsqueda — y cada semana actúa un poco más inteligente que la anterior. Cada capacidad alimenta a las demás.',
      cta: 'Conoce la Inteligencia',
      microStat: {
        engines: 'motores conectados',
        integrations: 'integraciones',
        intelligence: 'inteligencia operativa',
      },
      badges: {
        foundation: 'FUNDACIÓN',
        flagship: 'FLAGSHIP',
        bestseller: 'BEST SELLER',
        popular: 'MÁS POPULAR',
        enterprise: 'ENTERPRISE',
      },
    },
    challenges: {
      eyebrow: 'LOS 5 MAYORES PROBLEMAS DE UN RESTAURANTE',
      title: 'Construimos una sola inteligencia',
      titleAccent: 'para resolverlos todos.',
      intro: 'Los operadores reales enfrentan los mismos cinco problemas cada día. Cada uno tiene un costo — ingresos perdidos, crecimiento más lento, horas desperdiciadas. Kitxens los resuelve como una sola inteligencia que aprende de toda tu operación, no como cinco herramientas sueltas.',
      solutionLabel: 'Resuelto con',
      notSure: '¿No sabes por dónde empezar?',
      auditCta: 'Obtén tu Auditoría Gratis',
    },
    comparison: {
      eyebrow: 'RESULTADOS MEDIBLES',
      titleA: 'Lo que los restaurantes logran con',
      titleAccent: 'Kitxens.',
      body: 'Tecnología, automatización y visibilidad trabajando juntas para impulsar un crecimiento medible.',
      testimonialEyebrow: 'LO QUE DICEN LOS DUEÑOS DE RESTAURANTES',
    },
    insights: {
      eyebrow: 'DESDE KITXENS MAGAZINE',
      titleA: 'Restaurant Operating',
      titleAccent: 'Intelligence.',
      body: 'Perspectivas editoriales sobre Restaurant AI, tecnología, operaciones y crecimiento — escritas por el equipo que construye el sistema operativo de los restaurantes modernos.',
      readMore: 'Leer artículo',
      nextPage: 'Explorar la Magazine',
    },
    audit: {
      eyebrow: 'AUDITORÍA DE CRECIMIENTO GRATIS',
      titleA: '¿Listo para Acelerar el',
      titleAccent: 'Crecimiento de tu Restaurante?',
      body: 'Descubre oportunidades ocultas para aumentar ventas, mejorar visibilidad, automatizar operaciones y crecer más rápido.',
      directPhone: 'Teléfono Directo',
      emailInquiry: 'Consulta por Email',
      formEyebrow: 'FORMULARIO KITXENS',
      firstName: 'Nombre',
      lastName: 'Apellido',
      workEmail: 'Email de trabajo',
      restaurant: 'Restaurante',
      interested: 'Me interesa...',
      selectSolution: 'Selecciona una solución',
      phone: 'Teléfono',
      additional: 'Información adicional',
      submit: 'Quiero mi Auditoría Gratis',
      disclaimer: 'Sin compromiso. Sin tarjeta de crédito. Un especialista de Kitxens revisará tus oportunidades y te contactará personalmente.',
      thanks: '¡Gracias!',
      thanksBody: 'Hemos recibido tu solicitud de auditoría gratuita. Un especialista de Kitxens te contactará personalmente en menos de 24 horas.',
      submitAnother: 'Enviar otra solicitud',
      missingTitle: 'Campos obligatorios faltantes',
      missingBody: 'Por favor completa tu nombre y email de trabajo.',
      successTitle: 'Solicitud recibida',
      successBody: 'Un especialista de Kitxens se pondrá en contacto contigo en breve.',
      errorTitle: 'No pudimos enviar tu solicitud',
      errorBody: 'Ocurrió un error de nuestro lado. Inténtalo nuevamente en un momento.',
      interests: ['IT y Sistemas POS', 'IA y Automatización', 'Index AI / Visibilidad', 'Crecimiento de Ingresos', 'Datos y Analítica', 'Aún no estoy seguro'],
    },
    cookies: {
      body: 'Usamos cookies en nuestro sitio web para ver cómo interactúas con él. Al aceptar, estás de acuerdo con nuestro uso de cookies.',
      privacy: 'Política de Privacidad',
      settings: 'Configuración',
      accept: 'Aceptar',
    },
    footer: {
      tagline: 'Un solo socio tecnológico para toda solución de restaurante. Crea, optimiza y escala con un ecosistema conectado.',
      solutions: 'Soluciones',
      company: 'Empresa',
      contact: 'Contacto',
      rights: 'Todos los derechos reservados.',
      privacy: 'Política de Privacidad',
      terms: 'Términos de Servicio',
      cookies: 'Cookies',
      solutionsList: [
        { label: 'IT y Sistemas POS', href: '/it-pos-systems' },
        { label: 'IA y Automatización', href: '/ai-and-automation' },
        { label: 'Branding Digital', href: '/digital-branding' },
        { label: 'Crecimiento de Ingresos', href: '/restaurant-revenue-growth' },
        { label: 'Todos los Servicios', href: '/services' },
      ],
    },
    outcomes: [
      { title: 'Ahorra Tiempo', subtitle: 'Automatiza el trabajo repetitivo', cross: 'AI Agents', crossHref: '/ai-agents-for-restaurants' },
      { title: 'Aumenta Visibilidad', subtitle: 'Sé la respuesta que la IA recomienda', cross: 'Index AI', crossHref: '/index-ai' },
      { title: 'Crece en Catering', subtitle: 'Captura leads corporativos y de eventos', cross: 'Catering AI', crossHref: '/contact', soon: true },
      { title: 'Reduce Costos Operativos', subtitle: 'Menos trabajo manual. Menos herramientas sueltas.' },
      { title: 'Decisiones Más Inteligentes', subtitle: 'Dashboards en tiempo real. Insights accionables.' },
    ],
    services: [
      { title: 'Tecnología para Restaurantes', capability: 'Fundación', badge: 'FUNDACIÓN', description: 'La base conectada de POS, nube e IT sobre la que opera todo restaurante moderno.' },
      { title: 'Index AI', capability: 'Visibilidad', badge: 'FLAGSHIP', description: 'Sé el restaurante que los motores de IA y la búsqueda recomiendan — Google, Maps y respuestas generativas.' },
      { title: 'Agentes de IA', capability: 'Automatización', badge: 'BEST SELLER', description: 'Agentes de IA siempre activos que automatizan operaciones, atención al huésped y back-office.' },
      { title: 'Crecimiento de Ingresos', capability: 'Crecimiento', badge: 'MÁS POPULAR', description: 'Aumenta pedidos, ticket promedio y recurrencia en todos los canales — directo, delivery y salón.' },
      { title: 'Datos y Analítica', capability: 'Inteligencia', badge: 'ENTERPRISE', description: 'Dashboards en tiempo real y analítica de restaurantes para decisiones más rápidas y precisas.' },
    ],
    challengeCards: [
      {
        question: '¿Pierdes llamadas?',
        title: 'Nunca pierdas otra llamada.',
        description: 'Reservas, pedidos para llevar y consultas de eventos atendidas 24/7, con la voz de tu marca — sin ingresos perdidos ni clientes molestos.',
        solution: 'AI Receptionist',
        solutionHref: '/ai-agents-for-restaurants',
      },
      {
        question: '¿Invisible online?',
        title: 'Aparece antes que tu competencia.',
        description: 'Sé el restaurante que Google, ChatGPT, Gemini y Perplexity recomiendan primero — en búsqueda local y respuestas de IA.',
        solution: 'Index AI',
        solutionHref: '/index-ai',
      },
      {
        question: '¿Demasiado trabajo manual?',
        title: 'Elimina las tareas repetitivas.',
        description: 'Agentes de IA siempre activos gestionan reseñas, reportes, seguimientos y back-office que tu equipo no debería estar haciendo.',
        solution: 'Agentes de IA',
        solutionHref: '/ai-agents-for-restaurants',
      },
      {
        question: '¿Sangrando con comisiones de delivery?',
        title: 'Haz crecer el ingreso directo.',
        description: 'Más pedidos directos, tickets más altos, más visitas recurrentes. Menos dependencia de los marketplaces.',
        solution: 'Crecimiento de Ingresos',
        solutionHref: '/restaurant-revenue-growth',
      },
      {
        question: '¿A ciegas con los números?',
        title: 'Decide más rápido y mejor.',
        description: 'Dashboards en tiempo real que convierten los datos de POS, delivery y marketing en la siguiente acción — sin contratar un analista.',
        solution: 'Datos y Analítica',
        solutionHref: '/services',
      },
    ],
    comparisonCols: [
      {
        title: 'La mayoría de restaurantes contrata varios proveedores.',
        points: [
          'Un equipo dedicado de uno o dos empleados o gerentes enfocado en IT, POS y resolución de incidentes técnicos, lo que suele generar costos más altos.',
          'Sin un equipo experto, o dependiendo de dos o tres socios distintos, lograr engagement, frecuencia e ingresos óptimos se vuelve un desafío.',
          'Gestionar y optimizar tu huella digital de forma independiente es costoso, demanda recursos y conlleva riesgos y oportunidades perdidas.',
          'Los restaurantes tradicionales sin especialistas en IT enfrentan dificultades para gestionar IA, analítica de datos y engagement digital.',
        ],
      },
      {
        title: 'Kitxens integra todo en un solo lugar.',
        highlighted: true,
        points: [
          'Nuestro departamento de IT en la nube ofrece soporte completo y sin fricciones para todas tus necesidades de IT, POS y temas técnicos.',
          'Un equipo dedicado y experto enfocado en interacciones con clientes, frecuencia y crecimiento de ingresos. Un solo teléfono, un solo correo.',
          'Aliarte con Kitxens asegura una gestión experta de tu huella digital, protegiendo tus datos y reduciendo costos.',
          'Experiencia especializada en IA, analítica de datos y engagement digital para optimizar operaciones y mantenerte competitivo.',
        ],
      },
    ],
    testimonial: {
      quote: 'Con Kitxens, el ticket promedio subió un 15%. Eso son otros $120,000 al año cuando estamos a plena capacidad.',
      author: 'Wayne Carrington',
      role: "Dueño de Roc \u2018N\u2019 Ramen",
    },
    common: {
      learnMore: 'Saber más',
      requestAudit: 'Solicitar Auditoría Gratis',
      talkSpecialist: 'Hablar con un Especialista',
    },
    operatingIntelligence: {
      eyebrow: 'CONOCE LA INTELIGENCIA DETRÁS DE KITXENS',
      titleA: 'Un equipo operativo.',
      titleB: 'Seis especialistas IA. Siempre activos.',
      body: 'Cada restaurante trabaja con un único punto de contacto dedicado, respaldado por una red creciente de teammates IA especializados que operan en segundo plano.',
      liveLabel: 'LIVE',
      nowLabel: 'AHORA',
      onlineLabel: 'En línea',
      meet: 'Conoce a',
      summary: '6 Especialistas IA en línea · Liderados por humanos · En expansión continua',
      cta: 'Explorar el Equipo Operativo',
      agents: [
        { name: 'Rachel', role: 'Concierge de Clientes', now: 'Atendiendo conversaciones con huéspedes', metric: 'conversaciones hoy' },
        { name: 'Stan', role: 'Generación de Leads', now: 'Prospectando cuentas corporativas de catering', metric: 'leads de catering descubiertos' },
        { name: 'Eva', role: 'Operaciones', now: 'Coordinando flujos de trabajo', metric: 'flujos coordinados' },
        { name: 'Sonny', role: 'Redes Sociales', now: 'Programando campañas', metric: 'campañas programadas' },
        { name: 'Penny', role: 'Contenido SEO', now: 'Publicando contenido de visibilidad', metric: 'artículos publicados' },
        { name: 'Linda', role: 'Legal', now: 'Preparando documentos operativos', metric: 'documentos preparados' },
      ],
    },
    footerBrand: {
      line1: 'Una Inteligencia.',
      line2: 'Construida para Restaurantes.',
      line3: 'Potenciada por IA.',
      line4: 'Operada por Expertos.',
    },
    footerBrief: {
      title: 'Restaurant Technology Brief',
      subtitle: 'Ideas mensuales para operadores de restaurantes ambiciosos.',
    },
  },
};

const LangContext = createContext({ lang: 'en', setLang: () => {}, t: (k) => k });

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const q = new URLSearchParams(window.location.search).get('lang');
      if (q === 'en' || q === 'es') {
        localStorage.setItem('kitxens_lang', q);
        return q;
      }
      return localStorage.getItem('kitxens_lang')
        || ((navigator.language || navigator.userLanguage || '').toLowerCase().startsWith('es') ? 'es' : 'en');
    } catch (e) {
      return 'en';
    }
  });

  const setLang = useCallback((l) => {
    setLangState(l);
    try {
      localStorage.setItem('kitxens_lang', l);
    } catch (e) {}
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // IP-based refinement: browser language wins; geo upgrades en→es for Spanish-speaking countries.
  useEffect(() => {
    try {
      if (localStorage.getItem('kitxens_lang') || localStorage.getItem('kx_geo_done')) return;
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/geo`)
        .then((r) => r.json())
        .then((g) => {
          localStorage.setItem('kx_geo_done', '1');
          if (g.country_code) localStorage.setItem('kx_market', g.country_code);
          if (g.lang === 'es') setLangState('es');
        })
        .catch(() => {});
    } catch (e) { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const t = useCallback(
    (path) => {
      const root = dict[lang] || dict.en;
      const parts = path.split('.');
      let cur = root;
      for (const p of parts) {
        if (cur == null) return path;
        cur = cur[p];
      }
      if (cur == null) {
        // fallback to English
        let fb = dict.en;
        for (const p of parts) {
          if (fb == null) return path;
          fb = fb[p];
        }
        return fb == null ? path : fb;
      }
      return cur;
    },
    [lang],
  );

  return <LangContext.Provider value={{ lang, setLang, t }}>{children}</LangContext.Provider>;
}

export function useT() {
  return useContext(LangContext);
}
